import { Injectable } from '@angular/core';
import { ApiCompaniesAdapterService } from '@fullyops/data/companies/api-adapter.service';
import {
  CompanyAssignee,
  CompanyInput,
  CompanyType,
} from '@fullyops/data/companies/company';
import { MiniCompany } from '@fullyops/data/companies/mini-company';
import { createStore } from '@ngneat/elf';
import {
  addEntities,
  deleteAllEntities,
  selectAllEntities,
  setEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { map, Observable, Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CompaniesService {
  constructor(private api: ApiCompaniesAdapterService) {}

  private miniStore = createStore(
    { name: 'mini-companies-store' },
    withEntities<MiniCompany>({ idKey: 'id' }),
  );

  private suppliers = createStore(
    { name: 'suppliers' },
    withEntities<MiniCompany>({ idKey: 'id' }),
  );

  private assigneeStore = createStore(
    { name: 'potential-company-assignees' },
    withEntities<CompanyAssignee>({ idKey: 'id' }),
  );

  companies$ = this.miniStore.pipe(selectAllEntities());
  suppliers$ = this.suppliers.pipe(selectAllEntities());
  potentialAssignees$ = this.assigneeStore.pipe(selectAllEntities());

  clearMini() {
    this.miniStore.update(deleteAllEntities());
  }

  refreshMini() {
    this.api.getMini().subscribe((res) => {
      this.miniStore.update(setEntities(res));
    });
  }

  refreshMiniClients() {
    this.api.getMini('CLIENT').subscribe((res) => {
      this.miniStore.update(setEntities(res));
    });
  }

  refreshSuppliers() {
    this.api.getCompanies({ type: ['SUPPLIER', 'BOTH'] }).subscribe((res) => {
      this.suppliers.update(setEntities(res.results));
    });
  }

  refreshPotentialAssignees(): void {
    this.api
      .getPotentialAssignees()
      .subscribe((res) => this.assigneeStore.update(setEntities(res)));
  }

  clearPotentialAssignees(): void {
    this.assigneeStore.update(deleteAllEntities());
  }

  existsByNifAndType(nif: string, type: CompanyType): Observable<boolean> {
    return this.api.existsByNifAndType(nif, type);
  }

  create(input: CompanyInput): Observable<string> {
    return this.api.create(input).pipe(map((res) => res.id));
  }

  createAndStore(input: CompanyInput): Observable<string> {
    const sub = new Subject<string>();
    sub.subscribe((id) =>
      this.miniStore.update(
        addEntities({ id, name: input.name, nif: input.nif }),
      ),
    );
    this.create(input).subscribe(sub);
    return sub;
  }
}
